// Shows attachments inside the app: images, PDFs, video, audio and text, with arrows to step
// through the email's other files.
import { useEffect, useState } from "preact/hooks";
import type { AttachmentInfo } from "../../shared/types";
import { formatSize } from "../format";
import { Icon } from "./Icon";
import { IconButton, Modal, Spinner } from "./ui";

type Kind = "image" | "pdf" | "video" | "audio" | "text";

const TEXT_TYPES =
  /^(text\/|application\/(json|xml|x-yaml|yaml|javascript|x-sh|x-python|sql|csv|x-ndjson))/i;
const TEXT_NAMES = /\.(txt|md|csv|tsv|json|xml|ya?ml|log|ini|conf|toml|js|ts|py|sh|sql|css)$/i;
/** Plain text over this size is cut, so a huge log doesn't freeze the tab. */
const TEXT_LIMIT = 512 * 1024;

/** What the viewer can show this file as, or null when it can only be downloaded. */
export const previewKind = (attachment: AttachmentInfo): Kind | null => {
  if (attachment.scan?.status === "malicious") return null;
  const type = attachment.contentType.toLowerCase();
  if (/^image\/(png|jpe?g|gif|webp|avif|bmp)$/.test(type)) return "image";
  if (type === "application/pdf") return "pdf";
  if (type.startsWith("video/")) return "video";
  if (type.startsWith("audio/")) return "audio";
  if (type === "text/html") return null;
  if (TEXT_TYPES.test(type) || TEXT_NAMES.test(attachment.filename)) return "text";
  return null;
};

const TextPreview = ({ url }: { url: string }) => {
  const [text, setText] = useState<string | null>(null);
  const [cut, setCut] = useState(false);
  useEffect(() => {
    const controller = new AbortController();
    setText(null);
    fetch(url, { signal: controller.signal, credentials: "same-origin" })
      .then((response) => response.blob())
      .then(async (blob) => {
        setCut(blob.size > TEXT_LIMIT);
        setText(await blob.slice(0, TEXT_LIMIT).text());
      })
      .catch(() => {
        if (!controller.signal.aborted) setText("This file couldn't be loaded.");
      });
    return () => controller.abort();
  }, [url]);
  if (text === null) return <Spinner />;
  return (
    <pre class="viewer-text">
      {text}
      {cut && "\n\n… The rest of this file is only in the download."}
    </pre>
  );
};

export const AttachmentViewer = ({
  attachments,
  start,
  onClose,
}: {
  attachments: AttachmentInfo[];
  start: number;
  onClose: () => void;
}) => {
  const [index, setIndex] = useState(start);
  const attachment = attachments[index];
  const many = attachments.length > 1;
  const step = (by: number) =>
    setIndex((current) => (current + by + attachments.length) % attachments.length);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (!many) return;
      if (event.key === "ArrowRight") step(1);
      if (event.key === "ArrowLeft") step(-1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [many, attachments.length]);

  if (!attachment) return null;
  const kind = previewKind(attachment);
  const view = `/api/attachments/${attachment.id}`;
  const download = `${view}?download=1`;
  return (
    <Modal
      title={attachment.filename}
      onClose={onClose}
      wide
      footer={
        <div class="viewer-footer">
          <span class="muted small">
            {formatSize(attachment.size)}
            {many && ` · ${index + 1} of ${attachments.length}`}
          </span>
          <div class="viewer-actions">
            {many && <IconButton icon="chevronLeft" label="Previous" onClick={() => step(-1)} />}
            {many && <IconButton icon="chevronRight" label="Next" onClick={() => step(1)} />}
            <a class="button" href={download} download={attachment.filename}>
              <Icon name="download" size={16} /> Download
            </a>
          </div>
        </div>
      }
    >
      <div class={`viewer viewer-${kind ?? "none"}`} key={attachment.id}>
        {kind === "image" && <img src={view} alt={attachment.filename} />}
        {kind === "pdf" && <iframe src={view} title={attachment.filename} />}
        {kind === "video" && (
          // biome-ignore lint/a11y/useMediaCaption: mail attachments come without captions.
          <video src={view} controls autoPlay />
        )}
        {kind === "audio" && (
          // biome-ignore lint/a11y/useMediaCaption: mail attachments come without captions.
          <audio src={view} controls autoPlay />
        )}
        {kind === "text" && <TextPreview url={download} />}
        {kind === null && (
          <p class="muted">There's no preview for this kind of file. Download it to open it.</p>
        )}
      </div>
    </Modal>
  );
};
